import type { ExportFileIssue, ExportFlowStep, ExportGroupFailure } from '../types';

export type ExportLogStatus = 'success' | 'warning' | 'failure';
export type ExportLogMode = 'table' | 'model';

export interface ExportLogEntry {
  readonly id: string;
  readonly status: ExportLogStatus;
  readonly subject: string;
  readonly message: string;
  readonly stage?: string;
  readonly exceptionType?: string;
  readonly elementCount?: number;
  readonly elementIds?: readonly string[];
  readonly additionalElementCount?: number;
  readonly suggestedAction?: string;
}

export interface ExportRunLog {
  readonly completedAt: string;
  readonly mode: ExportLogMode;
  readonly outputPath: string;
  readonly traceId: string | null;
  readonly groupFailures: readonly ExportGroupFailure[];
  readonly failureNoticeEntries: readonly ExportLogEntry[];
  readonly hasSuccessfulOutput: boolean;
  readonly successful: number;
  readonly warnings: number;
  readonly failures: number;
  readonly entries: readonly ExportLogEntry[];
}

export interface ExportLogResultInput {
  readonly success: boolean;
  readonly error?: string;
  readonly failedStep?: ExportFlowStep;
  readonly traceId?: string;
  readonly filesWritten?: number;
  readonly successfulFiles?: readonly string[];
  readonly skippedFiles?: readonly string[];
  readonly fileIssues?: readonly ExportFileIssue[];
  readonly groupFailures?: readonly ExportGroupFailure[];
}

export interface BuildExportRunLogOptions {
  readonly completedAt: string;
  readonly mode: ExportLogMode;
  readonly outputPath: string;
}

const stepLabels = {
  license: 'License and quota check',
  calculate: 'Plugin-local calculation',
  record: 'Run recording',
  write: 'Disk write',
} as const satisfies Record<ExportFlowStep, string>;

function countStatus(entries: readonly ExportLogEntry[], status: ExportLogStatus): number {
  return entries.filter((entry) => entry.status === status).length;
}

export function buildExportRunLog(
  result: ExportLogResultInput,
  options: BuildExportRunLogOptions,
): ExportRunLog {
  const entries: ExportLogEntry[] = [];
  const failureNoticeEntries: ExportLogEntry[] = [];

  for (const [index, fileName] of (result.successfulFiles ?? []).entries()) {
    entries.push({
      id: `success-${index}-${fileName}`,
      status: 'success',
      subject: fileName,
      message: 'PCF file written successfully.',
    });
  }

  if (entries.length === 0 && result.success && (result.filesWritten ?? 0) > 0) {
    const count = result.filesWritten ?? 0;
    entries.push({
      id: 'success-summary',
      status: 'success',
      subject: `${count} PCF file${count === 1 ? '' : 's'}`,
      message: 'Written successfully. Individual filenames were not reported.',
    });
  }

  const detailedSkippedFiles = new Set(
    (result.fileIssues ?? [])
      .filter((issue) => !issue.fatal)
      .map((issue) => issue.fileName),
  );
  for (const [index, issue] of (result.fileIssues ?? []).entries()) {
    const entry: ExportLogEntry = {
      id: `${issue.fatal ? 'failure' : 'warning'}-file-${index}-${issue.fileName}`,
      status: issue.fatal ? 'failure' : 'warning',
      subject: issue.fileName || 'Unnamed PCF entry',
      message: issue.reason,
      stage: 'disk write',
      suggestedAction: issue.suggestedAction,
    };
    entries.push(entry);
    if (issue.fatal) {
      failureNoticeEntries.push(entry);
    }
  }

  for (const [index, fileName] of (result.skippedFiles ?? []).entries()) {
    if (detailedSkippedFiles.has(fileName)) {
      continue;
    }
    entries.push({
      id: `warning-${index}-${fileName}`,
      status: 'warning',
      subject: fileName || 'Unnamed PCF entry',
      message: 'The plugin skipped this file while writing to disk.',
      stage: 'disk write',
    });
  }

  for (const [index, failure] of (result.groupFailures ?? []).entries()) {
    const entry: ExportLogEntry = {
      id: `failure-${index}-${failure.systemName}`,
      status: 'failure',
      subject: failure.systemName,
      message: failure.error,
      stage: failure.stage,
      exceptionType: failure.exceptionType,
      elementCount: failure.elementCount,
      elementIds: failure.elementIds,
      additionalElementCount: failure.additionalElementCount,
      suggestedAction: failure.suggestedAction,
    };
    entries.push(entry);
    failureNoticeEntries.push(entry);
  }

  const hasFatalFileIssue = (result.fileIssues ?? []).some((issue) => issue.fatal);
  if (!result.success && (result.groupFailures?.length ?? 0) === 0 && !hasFatalFileIssue) {
    const entry: ExportLogEntry = {
      id: 'failure-run',
      status: 'failure',
      subject: result.failedStep ? stepLabels[result.failedStep] : 'Export run',
      message: result.error || 'The export did not complete.',
    };
    entries.push(entry);
    if (result.failedStep === 'write') {
      failureNoticeEntries.push(entry);
    }
  }

  return {
    completedAt: options.completedAt,
    mode: options.mode,
    outputPath: options.outputPath,
    traceId: result.traceId ?? null,
    groupFailures: [...(result.groupFailures ?? [])],
    failureNoticeEntries,
    hasSuccessfulOutput:
      (result.filesWritten ?? 0) > 0 || (result.successfulFiles?.length ?? 0) > 0,
    successful: countStatus(entries, 'success'),
    warnings: countStatus(entries, 'warning'),
    failures: countStatus(entries, 'failure'),
    entries,
  };
}

export function formatExportRunLog(log: ExportRunLog): string {
  const metadata = [
    'PCF export log',
    `Completed: ${new Date(log.completedAt).toLocaleString()}`,
    `Mode: ${log.mode === 'model' ? 'Selected in Revit model' : 'Export table'}`,
    `Output: ${log.outputPath || 'Not reported'}`,
    `Trace ID: ${log.traceId ?? 'Not reported'}`,
    `Summary: ${log.successful} succeeded, ${log.warnings} warnings, ${log.failures} failed`,
    '',
  ];
  const lines = log.entries.flatMap((entry) => {
    const details = [`[${entry.status.toUpperCase()}] ${entry.subject}: ${entry.message}`];
    if (entry.stage) {
      details.push(`  Stage: ${entry.stage}`);
    }
    if (entry.exceptionType) {
      details.push(`  Cause type: ${entry.exceptionType}`);
    }
    if (entry.elementCount !== undefined) {
      details.push(`  Affected Revit elements: ${entry.elementCount}`);
    }
    if ((entry.elementIds?.length ?? 0) > 0) {
      const additional = entry.additionalElementCount
        ? ` (+${entry.additionalElementCount} more)`
        : '';
      details.push(`  Revit element IDs: ${entry.elementIds!.join(', ')}${additional}`);
    }
    if (entry.suggestedAction) {
      details.push(`  Action: ${entry.suggestedAction}`);
    }
    return details;
  });
  return [...metadata, ...lines].join('\n');
}
